import { useGlobalMediaPlayer } from "@/hooks/useGlobalMediaPlayer";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { IVideoPlayerProps, IVideoPlayerRef } from "../index";

export const useVideoPlayerLogic = (
  props: IVideoPlayerProps,
  ref: React.ForwardedRef<IVideoPlayerRef>
) => {
  const {
    src,
    enableHoverPlay = false,
    disableClickPlay = false,
    onTimeUpdate,
    range,
    stopOnUnmount = true,
  } = props;

  const [isVideoEnabled, setIsVideoEnabled] = useState(
    !enableHoverPlay && !disableClickPlay
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const hoverTimerRef = useRef<NodeJS.Timeout>(undefined);
  const playPromiseRef = useRef<Promise<void>>(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { isPlaying, handlePlay, handlePause } = useGlobalMediaPlayer(videoRef);

  useImperativeHandle(ref, () => ({
    play: () => {
      if (videoRef.current && !playPromiseRef.current) {
        playPromiseRef.current = videoRef.current
          .play()
          .catch((e) => {
            console.error("Video play error:", e);
          })
          .finally(() => {
            playPromiseRef.current = undefined;
          });
      }
    },
    pause: () => {
      videoRef.current?.pause();
      playPromiseRef.current = undefined;
    },
    getVideoElement: () => videoRef.current,
  }));

  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;

  useEffect(() => {
    if (!isVideoEnabled || !videoRef.current) {
      return;
    }

    const currentVideo = videoRef.current;

    currentVideo.addEventListener("play", handlePlay);
    currentVideo.addEventListener("pause", handlePause);

    let onTimeUpdateFunc: (ev: Event) => unknown = () => {};
    if (onTimeUpdateRef.current) {
      onTimeUpdateFunc = (event: Event) => {
        const target = event.currentTarget as HTMLVideoElement;
        if (target) {
          onTimeUpdateRef.current?.(target.currentTime);
          if (
            range?.start &&
            range?.end &&
            range.start <= range.end &&
            (target.currentTime < range.start || target.currentTime > range.end)
          ) {
            target.currentTime = range.start;
          }
        }
      };
      currentVideo.addEventListener("timeupdate", onTimeUpdateFunc);
    }

    return () => {
      currentVideo.removeEventListener("play", handlePlay);
      currentVideo.removeEventListener("pause", handlePause);
      if (onTimeUpdateRef.current) {
        currentVideo.removeEventListener("timeupdate", onTimeUpdateFunc);
      }
    };
  }, [range?.start, range?.end, isVideoEnabled, handlePlay, handlePause]);

  useEffect(() => {
    if (!videoRef.current || !src) {
      return;
    }

    videoRef.current.load();
    setIsLoaded(false);
  }, [src]);

  useEffect(() => {
    const currentVideo = videoRef.current;
    return () => {
      if (!stopOnUnmount) return;

      // Clear any pending play promise - don't wait for it
      playPromiseRef.current = undefined;

      handlePause();

      if (typeof window !== "undefined" && hoverTimerRef.current) {
        window.clearTimeout(hoverTimerRef.current);
      }

      if (currentVideo) {
        try {
          currentVideo.pause();
          currentVideo.currentTime = 0;
        } catch (error) {
          console.error("Video cleanup error:", error);
        }
      }
    };
  }, [stopOnUnmount, handlePause]);

  // Always cleanup hoverTimerRef on unmount, regardless of stopOnUnmount setting
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = undefined;
      }
    };
  }, []);

  const handleStartPlay = useCallback(() => {
    if (!isPlaying && !playPromiseRef.current) {
      setIsVideoEnabled(true);
      // Clear any existing timer before setting a new one
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = undefined;
      }
      hoverTimerRef.current = setTimeout(() => {
        if (videoRef.current?.src) {
          playPromiseRef.current = videoRef.current
            .play()
            .catch((e) => {
              console.error("Video play error:", e);
            })
            .finally(() => {
              playPromiseRef.current = undefined;
            });
        }
      }, 500);
    }
  }, [isPlaying]);

  const handleStopPlay = useCallback(async () => {
    setIsVideoEnabled(false);
    handlePause();
    if (window && hoverTimerRef.current) {
      window?.clearTimeout(hoverTimerRef.current);
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (playPromiseRef.current) {
      await playPromiseRef.current;
      playPromiseRef.current = undefined;
    }
  }, [handlePause]);

  const handleMouseEnter = useCallback(() => {
    if (enableHoverPlay) {
      handleStartPlay();
    }
  }, [enableHoverPlay, handleStartPlay]);

  const handleMouseLeave = useCallback(() => {
    if (enableHoverPlay) {
      handleStopPlay();
    }
  }, [enableHoverPlay, handleStopPlay]);

  const handleExpand = useCallback(() => {
    videoRef.current?.requestFullscreen();
  }, []);

  return {
    videoRef,
    isLoaded,
    setIsLoaded,
    handleMouseEnter,
    handleMouseLeave,
    handleExpand,
    isPlaying,
  };
};
